/**
 * Canonical permission registry for tenant authorization.
 *
 * Single source of truth for the `<module>.<action>` permission strings that custom roles bundle and that the
 * application checks. Code authorizes by PERMISSION, never by role name. Parish is intentionally NOT a module:
 * a parish is just a branch, so parish access is the normal module permissions scoped to the parish branch.
 *
 * Standard modules expose view / edit / manage. Approval modules (finance, budgets) add `approve`.
 * Reports and settings are read-then-manage.
 */

import { RoleScope } from "../workspace/workspace.enums";

export enum PermissionModule {
  Members = "members",
  Activities = "activities",
  Assets = "assets",
  Forms = "forms",
  Groups = "groups",
  Ministries = "ministries",
  Departments = "departments",
  Reports = "reports",
  Settings = "settings",
  /** Settings for the one operational unit in a mono-branch workspace. */
  WorkspaceSettings = "workspace_settings",
  Users = "users",
  Finance = "finance",
  Budgets = "budgets",
  // Organization governance (workspace-scoped only — see MODULE_SCOPES).
  Branches = "branches",
  Roles = "roles",
  Subscription = "subscription"
}

export enum PermissionAction {
  View = "view",
  Edit = "edit",
  Approve = "approve",
  Manage = "manage"
}

const STANDARD: readonly PermissionAction[] = [PermissionAction.View, PermissionAction.Edit, PermissionAction.Manage];
const APPROVAL: readonly PermissionAction[] = [
  PermissionAction.View,
  PermissionAction.Edit,
  PermissionAction.Approve,
  PermissionAction.Manage
];
const READ_MANAGE: readonly PermissionAction[] = [PermissionAction.View, PermissionAction.Manage];

/** The actions each module exposes. Adding a future module means adding one entry here. */
export const MODULE_ACTIONS: Record<PermissionModule, readonly PermissionAction[]> = {
  [PermissionModule.Members]: STANDARD,
  [PermissionModule.Activities]: STANDARD,
  [PermissionModule.Assets]: STANDARD,
  [PermissionModule.Forms]: STANDARD,
  [PermissionModule.Groups]: STANDARD,
  [PermissionModule.Ministries]: STANDARD,
  [PermissionModule.Departments]: STANDARD,
  [PermissionModule.Users]: STANDARD,
  [PermissionModule.Reports]: READ_MANAGE,
  [PermissionModule.Settings]: READ_MANAGE,
  [PermissionModule.WorkspaceSettings]: READ_MANAGE,
  [PermissionModule.Finance]: APPROVAL,
  [PermissionModule.Budgets]: APPROVAL,
  [PermissionModule.Branches]: STANDARD,
  [PermissionModule.Roles]: READ_MANAGE,
  [PermissionModule.Subscription]: READ_MANAGE
};

/**
 * Which role scopes each module's permissions may be granted on. A role only holds permissions whose module
 * allows its scope. On a WORKSPACE role a shared permission applies organization-wide; on a BRANCH role it
 * applies to that branch. (Parish is not a scope here — a parish is a workspace-scoped assignment, i.e. the
 * SYSTEM branch, resolved at evaluation time; it never appears in role authoring.)
 *
 * - Organization-only: settings, branches, roles, subscription (governance / org config).
 * - Shared (both scopes): users, finance, budgets, reports, ministries.
 * - Branch-only: members, activities, assets, groups, departments, forms (operational).
 */
export const MODULE_SCOPES: Record<PermissionModule, readonly RoleScope[]> = {
  [PermissionModule.Settings]: [RoleScope.Workspace],
  // In a mono-branch workspace this is the role-grantable authority for that workspace's settings.
  // It is intentionally distinct from both `users` and organization-wide `settings`.
  [PermissionModule.WorkspaceSettings]: [RoleScope.Branch],
  [PermissionModule.Branches]: [RoleScope.Workspace],
  [PermissionModule.Roles]: [RoleScope.Workspace],
  [PermissionModule.Subscription]: [RoleScope.Workspace],
  [PermissionModule.Users]: [RoleScope.Workspace, RoleScope.Branch],
  [PermissionModule.Finance]: [RoleScope.Workspace, RoleScope.Branch],
  [PermissionModule.Budgets]: [RoleScope.Workspace, RoleScope.Branch],
  [PermissionModule.Reports]: [RoleScope.Workspace, RoleScope.Branch],
  [PermissionModule.Ministries]: [RoleScope.Workspace, RoleScope.Branch],
  [PermissionModule.Members]: [RoleScope.Branch],
  [PermissionModule.Activities]: [RoleScope.Branch],
  [PermissionModule.Assets]: [RoleScope.Branch],
  [PermissionModule.Groups]: [RoleScope.Branch],
  [PermissionModule.Departments]: [RoleScope.Branch],
  [PermissionModule.Forms]: [RoleScope.Branch]
};

/** Broad `<module>.<action>` shape. The authoritative valid set is ALL_PERMISSIONS / isPermission. */
export type Permission = `${PermissionModule}.${PermissionAction}`;

/** Every valid permission string, derived from MODULE_ACTIONS (the authoritative registry). */
export const ALL_PERMISSIONS: readonly Permission[] = (
  Object.entries(MODULE_ACTIONS) as Array<[PermissionModule, readonly PermissionAction[]]>
).flatMap(([module, actions]) => actions.map((action) => `${module}.${action}` as Permission));

const ALL_PERMISSIONS_SET = new Set<string>(ALL_PERMISSIONS);

/** Runtime guard: is this string a registered permission? */
export function isPermission(value: string): value is Permission {
  return ALL_PERMISSIONS_SET.has(value);
}

/** The permission strings for a single module. */
export function allPermissionsForModule(module: PermissionModule): readonly Permission[] {
  return MODULE_ACTIONS[module].map((action) => `${module}.${action}` as Permission);
}

/** Compose a permission string from a module + action. */
export function permission(module: PermissionModule, action: PermissionAction): Permission {
  return `${module}.${action}` as Permission;
}

/** Split a permission string back into its module + action (null if not a registered permission). */
export function parsePermission(value: string): { module: PermissionModule; action: PermissionAction } | null {
  if (!isPermission(value)) return null;
  const [module, action] = value.split(".") as [PermissionModule, PermissionAction];
  return { module, action };
}

/**
 * True when a module's permissions may be granted on the given role scope. `scope` is accepted as either the
 * shared `RoleScope` enum or its raw string value, so the backend (Prisma's nominally-distinct `RoleScope`
 * enum, same string values) and the frontend can both call it without a cast.
 */
export function isModuleAllowedForScope(module: PermissionModule, scope: RoleScope | string): boolean {
  return (MODULE_SCOPES[module] as readonly string[]).includes(String(scope));
}

/** True when a permission string may be granted on the given role scope. */
export function isPermissionAllowedForScope(value: string, scope: RoleScope | string): boolean {
  const parsed = parsePermission(value);
  return parsed ? isModuleAllowedForScope(parsed.module, scope) : false;
}

/** Every permission valid on the given role scope (drives scope-filtered role authoring). */
export function permissionsForScope(scope: RoleScope | string): readonly Permission[] {
  return ALL_PERMISSIONS.filter((value) => isPermissionAllowedForScope(value, scope));
}

/**
 * Department access roles (seeded, scope = DEPARTMENT). These are the SAME permission strings as everywhere else —
 * a department role is just a permission bundle evaluated through a department membership (the `departmentId`
 * condition on the CASL grant provides the scoping). They are NOT shown in the global Role Management UI.
 *
 * Keep this list and the seeded `Role.key` values in lock-step; the backend seeder and the frontend label map both
 * key off these. Titles (President/Treasurer…) are organizational and live on DepartmentMember — never here.
 */
export const DEPARTMENT_ROLE_KEYS = [
  "department-manager",
  "department-finance-manager",
  "department-activity-manager",
  "department-finance-viewer",
  "department-member"
] as const;

export type DepartmentRoleKey = (typeof DEPARTMENT_ROLE_KEYS)[number];

export type DepartmentRolePreset = {
  key: DepartmentRoleKey;
  /** The bundled permission strings (department-scoped at evaluation time). */
  permissions: readonly Permission[];
};

const P = permission;

/** The seeded department role bundles. Reuses existing module permissions; no new permission grammar. */
export const DEPARTMENT_ROLE_PRESETS: Record<DepartmentRoleKey, DepartmentRolePreset> = {
  "department-manager": {
    key: "department-manager",
    permissions: [
      P(PermissionModule.Departments, PermissionAction.Manage),
      P(PermissionModule.Finance, PermissionAction.Manage),
      P(PermissionModule.Activities, PermissionAction.Manage),
      P(PermissionModule.Members, PermissionAction.View)
    ]
  },
  "department-finance-manager": {
    key: "department-finance-manager",
    permissions: [
      P(PermissionModule.Finance, PermissionAction.Manage),
      P(PermissionModule.Departments, PermissionAction.View)
    ]
  },
  "department-activity-manager": {
    key: "department-activity-manager",
    permissions: [
      P(PermissionModule.Activities, PermissionAction.Manage),
      P(PermissionModule.Departments, PermissionAction.View)
    ]
  },
  "department-finance-viewer": {
    key: "department-finance-viewer",
    permissions: [
      P(PermissionModule.Finance, PermissionAction.View),
      P(PermissionModule.Departments, PermissionAction.View)
    ]
  },
  "department-member": {
    key: "department-member",
    permissions: [P(PermissionModule.Departments, PermissionAction.View)]
  }
};

/** Runtime guard: is this a known department role key? */
export function isDepartmentRoleKey(value: string): value is DepartmentRoleKey {
  return (DEPARTMENT_ROLE_KEYS as readonly string[]).includes(value);
}

export * from "./tenant-authorization";
